
import { useState, useEffect } from "react";
import usePageMetadata from "@/hooks/usePageMetadata";
import useScrollToTop from "@/hooks/useScrollToTop";
import { useAuth } from "@/helpers";
import { useDemoEvents } from "@/hooks/useDemoEvents";
import { useCalendarNavigation } from "@/hooks/useCalendarNavigation";
import { useGeolocation } from "@/hooks/useGeolocation";
import { useIsAdmin } from "@/hooks/useUserRole";
import { useIsMobile } from "@/hooks/use-mobile";
import { DemoEvent, DemoEventInput, CategoryFilter as CategoryFilterType } from "@/types/demo-calendar";
import CalendarGrid from "@/components/demo-calendar/CalendarGrid";
import CalendarList from "@/components/demo-calendar/CalendarList";
import CategoryFilter from "@/components/demo-calendar/CategoryFilter";
import ProximityFilter from "@/components/demo-calendar/ProximityFilter";
import AddEventModal from "@/components/demo-calendar/AddEventModal";
import { useNavigate } from "react-router-dom";
import { buildDemoEventPath } from "@/utils/eventSlug";
import { PUBLIC_ROUTE_META } from "@/lib/seo/publicMetadata";
import { DEMO_EVENT_CATEGORY_FILTERS } from "@/utils/demoEventPresentation";
import { trackEvent } from "@/utils/tracking";

const DemoCalendarPage = () => {
  useScrollToTop();

  usePageMetadata(PUBLIC_ROUTE_META["/demo-calendar"]);
  const { isAuthenticated } = useAuth();
  const { isAdmin, isLoading: isLoadingRole } = useIsAdmin();
  const navigate = useNavigate();
  const { currentDate, goToPreviousMonth, goToNextMonth, goToToday } = useCalendarNavigation();
  const { events, createEvent, updateEvent, deleteEvent, isCreating, isUpdating, isDeleting } = useDemoEvents();
  const isMobile = useIsMobile();

  const [viewMode, setViewMode] = useState<'calendar' | 'list'>(isMobile ? 'list' : 'calendar');

  useEffect(() => {
    setViewMode(isMobile ? 'list' : 'calendar');
  }, [isMobile]);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<DemoEvent | null>(null);
  const [proximityRadius, setProximityRadius] = useState<number | null>(null);

  // Location is only requested when the user picks a radius, so the browser
  // permission prompt never fires on page load.
  const {
    latitude,
    longitude,
    loading: locationLoading,
    permissionDenied,
    permissionState,
    requestLocation,
  } = useGeolocation();
  const hasUserLocation = latitude !== null && longitude !== null;

  const handleRadiusChange = (radius: number | null) => {
    setProximityRadius(radius);
    if (radius !== null && !hasUserLocation && !locationLoading) {
      requestLocation();
    }
  };

  // Drop the radius if the location request fails so the filter does not
  // look active while every event is still shown.
  useEffect(() => {
    if (permissionState === 'denied') {
      setProximityRadius(null);
    }
  }, [permissionState]);

  const [categoryFilters, setCategoryFilters] = useState<CategoryFilterType[]>(() =>
    DEMO_EVENT_CATEGORY_FILTERS.map((filter) => ({ ...filter })),
  );

  const handleToggleCategory = (category: string) => {
    setCategoryFilters(prev =>
      prev.map(filter =>
        filter.category === category
          ? { ...filter, enabled: !filter.enabled }
          : filter
      )
    );
  };

  const handleAddEvent = () => {
    trackEvent("demo_event_create_started", {
      surface: "demo_calendar",
      is_authenticated: isAuthenticated,
      is_admin: isAdmin,
    });

    if (!isAuthenticated) {
      trackEvent("demo_event_create_failed_unauthenticated", {
        failure_reason: "redirect_to_signin",
      });
      navigate("/auth/signin");
      return;
    }
    if (!isAdmin) {
      return; // Only admins can add events
    }
    setIsAddModalOpen(true);
  };

  const handleEditEvent = (event: DemoEvent) => {
    if (!isAuthenticated) {
      navigate("/auth/signin");
      return;
    }
    if (!isAdmin) {
      return; // Only admins can edit events
    }
    setEditingEvent(event);
  };

  const handleSubmitEvent = (eventData: DemoEventInput) => {
    if (editingEvent) {
      updateEvent({ id: editingEvent.id, eventData });
      setEditingEvent(null);
    } else {
      createEvent(eventData);
      setIsAddModalOpen(false);
    }
  };

  const handleDeleteEvent = (eventId: string) => {
    if (!isAdmin) {
      return; // Only admins can delete events
    }
    if (window.confirm('Are you sure you want to delete this event?')) {
      deleteEvent(eventId);
    }
  };

  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    setEditingEvent(null);
  };

  const handleEventClick = (event: DemoEvent) => {
    navigate(buildDemoEventPath(event));
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Demo Calendar</h1>
        <p className="text-muted-foreground">
          Schedule and manage demo events for outdoor gear rentals.
        </p>
      </div>

      {/* Uncomment this section if you want to show a sign-in prompt for unauthenticated users */}
      {/* {!isAuthenticated && (
        <div className="mb-8 p-4 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground mb-3">
            Sign in to add/edit demo events.
          </p>
          <Button onClick={() => navigate("/auth/signin")}>
            Sign In
          </Button>
        </div>
      )} */}

      {isAuthenticated && !isLoadingRole && !isAdmin && (
        <div className="mb-8 p-4 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">
            Only administrators can create and manage demo events.
          </p>
        </div>
      )}

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-4">
          <ProximityFilter
            selectedRadius={proximityRadius}
            onRadiusChange={handleRadiusChange}
            hasUserLocation={hasUserLocation}
            loading={locationLoading}
            permissionDenied={permissionDenied}
            locationUnavailable={permissionState === 'denied'}
          />

          <CategoryFilter
            filters={categoryFilters}
            onToggle={handleToggleCategory}
          />
        </div>

        {/* Calendar / List */}
        <div className="lg:col-span-3 min-w-0">
          {viewMode === 'calendar' ? (
            <CalendarGrid
              currentDate={currentDate}
              events={events}
              categoryFilters={categoryFilters}
              proximityRadius={proximityRadius}
              userLatitude={latitude}
              userLongitude={longitude}
              onPreviousMonth={goToPreviousMonth}
              onNextMonth={goToNextMonth}
              onGoToToday={goToToday}
              onAddEvent={handleAddEvent}
              viewMode={viewMode}
              onChangeView={setViewMode}
              onEditEvent={handleEditEvent}
              onDeleteEvent={handleDeleteEvent}
              onEventClick={handleEventClick}
              isDeleting={isDeleting}
              isAdmin={isAdmin}
              isLoadingRole={isLoadingRole}
            />
          ) : (
            <CalendarList
              currentDate={currentDate}
              events={events}
              categoryFilters={categoryFilters}
              proximityRadius={proximityRadius}
              userLatitude={latitude}
              userLongitude={longitude}
              onPreviousMonth={goToPreviousMonth}
              onNextMonth={goToNextMonth}
              onGoToToday={goToToday}
              onAddEvent={handleAddEvent}
              viewMode={viewMode}
              onChangeView={setViewMode}
              onEditEvent={handleEditEvent}
              onDeleteEvent={handleDeleteEvent}
              onEventClick={handleEventClick}
              isDeleting={isDeleting}
              isAdmin={isAdmin}
              isLoadingRole={isLoadingRole}
            />
          )}
        </div>
      </div>

      {/* Add/Edit Event Modal */}
      <AddEventModal
        open={isAddModalOpen || !!editingEvent}
        onClose={handleCloseModal}
        onSubmit={handleSubmitEvent}
        editEvent={editingEvent}
        isSubmitting={isCreating || isUpdating}
      />
    </div>
  );
};

export default DemoCalendarPage;
